import React, { useState, useEffect } from 'react';
import {
  BookOpen, Clock, CheckCircle2, Sparkles, X,
  CreditCard, Lock, Play, FileText, HelpCircle, Layers, Award,
  ChevronDown, ChevronRight
} from 'lucide-react';
import { normalizeCourse, localCourseResumen } from '../../utils/courseStructure';
import '../../styles/CursosVortex.css';

export const CourseDetailModal = ({
  course,
  isOpen,
  onClose,
  isSubscribed,
  isAuthenticated,
  onEnroll,
  onStartCourse
}) => {
  if (!isOpen || !course) return null;

  const normCourse = normalizeCourse(course);
  const resumen = localCourseResumen(normCourse);
  const temas = normCourse?.temas || [];
  const temasConFinal = temas.filter((t) => (t.examen_final?.questions?.length || 0) > 0);

  const [expandedTemas, setExpandedTemas] = useState({});

  useEffect(() => {
    if (temas.length > 0) {
      const allOpen = {};
      temas.forEach((t, i) => { allOpen[t.id || i] = true; });
      setExpandedTemas(allOpen);
    }
  }, [normCourse?.id]);

  const toggleTema = (id) => {
    setExpandedTemas((prev) => ({
      ...prev,
      [id]: prev[id] === undefined ? false : !prev[id]
    }));
  };

  const expandAll = () => {
    const all = {};
    temas.forEach((t, i) => { all[t.id || i] = true; });
    setExpandedTemas(all);
  };

  const collapseAll = () => {
    const all = {};
    temas.forEach((t, i) => { all[t.id || i] = false; });
    setExpandedTemas(all);
  };

  return (
    <div className="vx-modal-overlay" onClick={onClose}>
      <div className="vx-modal-card" onClick={(e) => e.stopPropagation()}>

        {/* ── Header con Imagen ── */}
        <div className="vx-modal-cover">
          <img src={course.thumbnail} alt={course.title} />
          <div className="vx-modal-cover-gradient" />

          <button className="btn-vx-modal-close" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>

          <div className="vx-modal-badges">
            <span className="vx-modal-badge-tag">{course.category}</span>
            <span className="vx-modal-badge-tag period">
              🗓️ {course.periodName || 'Ciclo Intensivo Admisión 2026'}
            </span>
          </div>

          <div className="vx-modal-cover-title">
            <h2>{course.title}</h2>
          </div>
        </div>

        {/* ── Cuerpo Modal ── */}
        <div className="vx-modal-body">

          {/* Métricas */}
          <div className="vx-modal-metrics">
            <div className="vx-modal-metric-item">
              <div className="vx-modal-metric-icon">
                <BookOpen className="w-5 h-5" />
              </div>
              <div>
                <span className="vx-modal-metric-label">Contenido</span>
                <span className="vx-modal-metric-val">
                  {resumen.temas} Módulos · {resumen.lecciones} Lecciones
                </span>
              </div>
            </div>

            <div className="vx-modal-metric-item">
              <div className="vx-modal-metric-icon" style={{ color: 'var(--gold-light)' }}>
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <span className="vx-modal-metric-label">Duración Estimada</span>
                <span className="vx-modal-metric-val">{course.duration_minutes || 90} Minutos</span>
              </div>
            </div>

            <div className="vx-modal-metric-item">
              <div className="vx-modal-metric-icon" style={{ color: '#fbbf24', background: 'rgba(245, 158, 11, 0.1)' }}>
                <CreditCard className="w-5 h-5" />
              </div>
              <div>
                <span className="vx-modal-metric-label">Inversión</span>
                <span className="vx-modal-metric-val" style={{ color: '#fbbf24', fontFamily: 'var(--font-tactical)' }}>
                  $300 MXN <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 400 }}>(4 Cursos)</span>
                </span>
              </div>
            </div>

            <div className="vx-modal-metric-item">
              <div className="vx-modal-metric-icon" style={{ color: '#34d399', background: 'rgba(16, 185, 129, 0.1)' }}>
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <span className="vx-modal-metric-label">Acceso</span>
                <span className="vx-modal-metric-val">
                  {isSubscribed ? '✅ Desbloqueado' : '🔒 Requiere Paquete'}
                </span>
              </div>
            </div>
          </div>

          {/* Descripción */}
          <div>
            <h3 className="vx-modal-sec-title">📋 Descripción y Enfoque Ceneval</h3>
            <p className="vx-modal-desc-box">{course.description}</p>
          </div>

          {/* Temario */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              <div>
                <h3 className="vx-modal-sec-title" style={{ margin: 0 }}>
                  <span>📚 Temario Completo del Curso ({resumen.temas} módulos · {resumen.lecciones} lecciones evaluadas)</span>
                </h3>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace', fontWeight: 400 }}>
                  Alineado al temario oficial Ceneval EXANI-II 2026
                </span>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  type="button"
                  onClick={expandAll}
                  style={{
                    background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.3)',
                    color: 'var(--gold-matte)', fontSize: '0.68rem', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
                    fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px'
                  }}
                >
                  <ChevronDown className="w-3 h-3" />
                  <span>Desplegar todos</span>
                </button>
                <button
                  type="button"
                  onClick={collapseAll}
                  style={{
                    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
                    color: 'var(--text-secondary)', fontSize: '0.68rem', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
                    fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px'
                  }}
                >
                  <ChevronRight className="w-3 h-3" />
                  <span>Contraer todos</span>
                </button>
              </div>
            </div>

            <div className="vx-modal-lessons-list">
              {temas.length > 0 ? (
                temas.map((tema, tIdx) => {
                  const temaKey = tema.id || tIdx;
                  const isExpanded = expandedTemas[temaKey] !== false;
                  return (
                    <div key={temaKey} className="vx-modal-lesson-card" style={{ borderColor: 'rgba(197,160,89,0.35)' }}>
                      <div
                        className="vx-modal-lesson-card-head"
                        onClick={() => toggleTema(temaKey)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title={isExpanded ? 'Clic para contraer módulo' : 'Clic para desplegar módulo'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className="vx-modal-lesson-num" style={{ background: 'rgba(197,160,89,0.2)', color: 'var(--gold-matte)' }}>
                            {tIdx + 1}
                          </span>
                          <h4 style={{ color: 'var(--gold-matte)', margin: 0 }}>{tema.title}</h4>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                            <Layers className="w-3.5 h-3.5" style={{ color: 'var(--gold-matte)' }} />
                            <span>{tema.lessons?.length || 0} lecciones · {tema.lessons?.reduce((a, l) => a + (l.quiz?.length ? 1 : 0), 0) || 0} quizes Ceneval</span>
                          </span>
                          <span style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: '24px', height: '24px', borderRadius: '6px',
                            background: 'rgba(197,160,89,0.15)', color: 'var(--gold-matte)', flexShrink: 0
                          }}>
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div
                          className="vx-modal-lessons-scrollable"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            padding: '6px 2px',
                            height: 'auto',
                            maxHeight: (tema.lessons?.length || 0) > 4 ? '500px' : 'none',
                            overflowY: (tema.lessons?.length || 0) > 4 ? 'auto' : 'visible'
                          }}
                        >
                          {(tema.lessons || []).map((lesson, li) => (
                            <div key={lesson.id || li} style={{
                              padding: '12px 16px', borderRadius: '10px', background: 'rgba(10,26,42,0.75)',
                              border: '1px solid rgba(197,160,89,0.18)', display: 'flex', flexDirection: 'column', gap: '8px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px', lineHeight: 1.35 }}>
                                  <Play className="w-4 h-4" style={{ color: 'var(--gold-matte)', flexShrink: 0 }} />
                                  <span>{lesson.title}</span>
                                </span>
                                <span style={{
                                  display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem',
                                  color: 'var(--gold-matte)', fontFamily: 'monospace', flexShrink: 0,
                                  background: 'rgba(197,160,89,0.1)', padding: '3px 8px', borderRadius: '6px'
                                }}>
                                  <Clock className="w-3.5 h-3.5" />
                                  {Math.round((lesson.duration_seconds || 300) / 60)} min
                                </span>
                              </div>

                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                                {lesson.pdf_resources?.length > 0 && (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(239,68,68,0.1)', color: '#fca5a5', padding: '2px 8px', borderRadius: '4px' }}>
                                    <FileText className="w-3.5 h-3.5" style={{ color: '#f87171' }} />
                                    {lesson.pdf_resources.length} PDF descargable
                                  </span>
                                )}
                                {lesson.quiz?.length > 0 && (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(245,158,11,0.1)', color: '#fde68a', padding: '2px 8px', borderRadius: '4px' }}>
                                    <HelpCircle className="w-3.5 h-3.5" style={{ color: '#fbbf24' }} />
                                    {lesson.quiz.length} Reactivos Ceneval (3 opciones)
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  Cargando lecciones del curso...
                </p>
              )}
            </div>
          </div>

          {/* Garantía y Acceso */}
          <div style={{
            padding: '1rem',
            borderRadius: '12px',
            background: 'rgba(10, 26, 42, 0.8)',
            border: '1px solid rgba(197, 160, 89, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{ padding: '10px', borderRadius: '8px', background: 'rgba(197, 160, 89, 0.1)', color: 'var(--gold-matte)' }}>
              <Lock className="w-6 h-6" />
            </div>
            <div>
              <h4 style={{ fontFamily: 'var(--font-tactical)', fontSize: '0.78rem', color: '#fff', margin: '0 0 2px' }}>
                Acceso Integral · Paquete 4 Cursos EXANI-II ($300 MXN)
              </h4>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                Al adquirir el periodo activo obtienes acceso simultáneo a los 4 cursos fundamentales (Pensamiento Matemático, Comprensión Lectora, Redacción Indirecta e Inglés). Al finalizar este periodo, el acceso concluirá de manera automática.
              </p>
            </div>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="vx-modal-footer-bar">
          <div>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block', fontFamily: 'monospace' }}>Paquete Completo Ciclo 2026:</span>
            <span className="vx-modal-price-tag">$300 MXN</span>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'monospace', marginLeft: '6px' }}>/ periodo activo · incluye los 4 cursos</span>
          </div>

          <div className="vx-modal-footer-btns">
            <button className="btn-card-resumen" onClick={onClose} style={{ padding: '10px 20px' }}>
              Cerrar
            </button>

            {isSubscribed ? (
              <button
                className="btn-card-enter"
                onClick={() => { onClose(); onStartCourse(course); }}
                style={{ padding: '10px 22px' }}
              >
                <Play className="w-4 h-4" />
                <span>Ingresar al Curso</span>
              </button>
            ) : (
              <button
                className="btn-card-buy"
                onClick={() => { onClose(); onEnroll(course); }}
                style={{ padding: '10px 22px' }}
              >
                <CreditCard className="w-4 h-4" />
                <span>{isAuthenticated ? 'Adquirir Paquete (4 Cursos) — $300 MXN' : 'Iniciar Sesión para Comprar'}</span>
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};
